'use client';

import { TMA, TELEGRAM_ENV } from '@ywwwtseng/tma-react-kit';
import { ScreenType } from '@ywwwtseng/react-kit';
import { Gem, ChevronLeft, PiggyBank } from 'lucide-react';
import { Account } from '@/components/common/Account';
import { Logo } from '@/components/common/Logo';
import { ConnectWalletButton } from '@/components/common/ConnectWalletButton';
import { BuyMooniniButton } from '@/components/common/BuyMooniniButton';
import { SyncWalletAddress } from '@/components/common/SyncWalletAddress';
import { RedPackage } from '@/components/icons';
import EarnScreen from '@/screens/tma/earn';
import GiftHistoryScreen from '@/screens/tma/gift-history';
import CreateGiftScreen from '@/screens/tma/create-gift';
import GiftScreen from '@/screens/tma/gift';
import GiftActivityScreen from '@/screens/tma/gift-activity';
import ClaimGiftScreen from '@/screens/tma/claim-gift';
import ShareGiftScreen from '@/screens/tma/share-gift';
import ChatGrowthGiftScreen from '@/screens/tma/chat-growth-gift';
import BalanceScreen from '@/screens/tma/balance';
import TokenScreen from '@/screens/tma/token';
import MyScreen from '@/screens/tma/my';
import InvestorScreen from '@/screens/tma/investor';
import InvestmentScreen from '@/screens/tma/investment';
import AccountActivityScreen from '@/screens/tma/account-activity';
import WalletBindingRecordsScreen from '@/screens/tma/wallet-binding-records';
import AccountVerification0Screen from '@/screens/tma/account-verification-0';
import AccountVerification1Screen from '@/screens/tma/account-verification-1';
import AccountVerification2Screen from '@/screens/tma/account-verification-2';
import WithdrawScreen from '@/screens/tma/withdraw';
import WithdrawConfirmScreen from '@/screens/tma/withdraw-confirm';
import DepositScreen from '@/screens/tma/deposit';
import AddChatScreen from '@/screens/tma/add-chat';
import AddTokenScreen from '@/screens/tma/add-token';
import GroupChannelScreenSettings from '@/screens/tma/group-channel-settings';
import BlockedScreen from '@/screens/tma/blocked';
import locales from '@/locales';
import { Ad } from '@/lib/ad/client';

const styles = {
  header: {
    backdropFilter: 'blur(3px)',
  },
  tabBar: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(3px)',
  },
};

const screens = {
  Home: {
    title: 'screens.earn',
    type: ScreenType.PAGE,
    screen: EarnScreen,
  },
  Balance: {
    title: 'screens.balance',
    type: ScreenType.PAGE,
    screen: BalanceScreen,
  },
  CreateGift: {
    title: 'screens.create_gift',
    type: ScreenType.PAGE,
    screen: CreateGiftScreen,
  },
  My: {
    title: 'screens.my',
    type: ScreenType.PAGE,
    screen: MyScreen,
  },
  ClaimGift: {
    title: 'screens.claim_gift',
    type: ScreenType.SINGLE,
    screen: ClaimGiftScreen,
    back: {
      title: 'common.more_gifts',
      replace: 'Home',
    },
  },
  ShareGift: {
    title: 'screens.share_gift',
    type: ScreenType.SINGLE,
    screen: ShareGiftScreen,
    back: {
      title: 'screens.balance',
      replace: 'Balance',
    },
  },
  ChatGrowthGift: {
    title: 'screens.chat_growth_gift',
    type: ScreenType.SINGLE,
    screen: ChatGrowthGiftScreen,
    back: {
      title: 'screens.balance',
      replace: 'Balance',
    },
  },
  Gifts: {
    title: 'screens.gift_history',
    type: ScreenType.DRAWER,
    screen: GiftHistoryScreen,
  },
  Gift: {
    title: 'screens.gift',
    type: ScreenType.DRAWER,
    screen: GiftScreen,
  },
  GiftActivity: {
    title: 'screens.gift_activity',
    type: ScreenType.DRAWER,
    screen: GiftActivityScreen,
  },
  Token: {
    title: 'screens.token',
    type: ScreenType.DRAWER,
    screen: TokenScreen,
  },
  AccountActivity: {
    title: 'settings.account_activity',
    type: ScreenType.DRAWER,
    screen: AccountActivityScreen,
  },
  WalletBindingRecords: {
    title: 'screens.wallet_binding_records',
    type: ScreenType.DRAWER,
    screen: WalletBindingRecordsScreen,
  },
  Investor: {
    title: 'investor.title',
    type: ScreenType.DRAWER,
    screen: InvestorScreen,
  },
  Investment: {
    title: 'investor.transfer',
    type: ScreenType.DRAWER,
    screen: InvestmentScreen,
    back: {
      replace: 'Investor',
    },
  },
  AccountVerification0: {
    title: 'screens.account_verification',
    type: ScreenType.DRAWER,
    screen: AccountVerification0Screen,
  },
  AccountVerification1: {
    title: 'screens.account_verification',
    type: ScreenType.DRAWER,
    screen: AccountVerification1Screen,
    back: {
      replace: 'AccountVerification0',
    },
  },
  AccountVerification2: {
    title: 'screens.account_verification',
    type: ScreenType.DRAWER,
    screen: AccountVerification2Screen,
    back: {
      replace: 'AccountVerification1',
    },
  },
  Withdraw: {
    title: 'screens.withdraw',
    type: ScreenType.DRAWER,
    screen: WithdrawScreen,
  },
  WithdrawConfirm: {
    title: 'screens.withdraw_confirm',
    type: ScreenType.DRAWER,
    screen: WithdrawConfirmScreen,
  },
  Deposit: {
    title: 'screens.deposit',
    type: ScreenType.DRAWER,
    screen: DepositScreen,
  },
  AddChat: {
    title: 'screens.add_chat',
    type: ScreenType.DRAWER,
    screen: AddChatScreen,
  },
  AddToken: {
    title: 'screens.add_token',
    type: ScreenType.DRAWER,
    screen: AddTokenScreen,
  },
  GroupChannel: {
    title: 'screens.group_channel_settings',
    type: ScreenType.DRAWER,
    screen: GroupChannelScreenSettings,
  },
  Blocked: {
    title: 'screens.blocked',
    type: ScreenType.PAGE,
    screen: BlockedScreen,
  },
};

const tabs = [
  {
    name: 'Home',
    title: 'screens.earn',
    icon: <Gem width={26} height={26} />,
  },
  {
    name: 'CreateGift',
    title: 'screens.create_gift',
    icon: <RedPackage width={26} height={26} />,
  },
  {
    name: 'Balance',
    title: 'screens.balance',
    icon: <PiggyBank width={28} height={28} />,
  },
  {
    name: 'My',
    title: 'screens.my',
    icon: <Account.Avatar size={28} />,
  },
];

const plugins = [
  {
    provider: Ad,
    options: {
      providers: {
        gigapub: {
          projectId: 5518,
        },
        monetag: {
          projectId: '10576413',
        },
        // onclicka: {
        //   projectId: '423381',
        //   spotId: '6109916',
        // },
      },
    },
  },
  {
    provider: SyncWalletAddress,
  }
];

export default function Page() {
  return (
    <TMA
      url="/api"
      env={
        process.env.NODE_ENV === 'development'
          ? TELEGRAM_ENV.MOCK
          : TELEGRAM_ENV.DEFAULT
      }

      launchScreen={
        <div className="v-stack">
          <div className="w-19 h-19">
            <Logo />
          </div>
          <div className="text-xl font-bold mt-2">moonpacket</div>
        </div>
      }
      screens={screens}
      onLoaded={() => {
        document.body.classList.add('loaded');
      }}
      i18nProps={{
        locales,
      }}
      layoutProps={{
        styles,
        tabs,
        backIcon: <ChevronLeft width={28} height={28} />,
        backText: 'common.back',
        headerRight: (route) => {
          if (!tabs.map((tab) => tab.name).includes(route.name)) {
            return null;
          }
          return (
            <div className="h-stack items-center gap-2">
              <BuyMooniniButton />
              <ConnectWalletButton />
            </div>
          );
        },
      }}
      plugins={plugins}
    >
    </TMA>
  );
}
